import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { TipTapEditor } from '../components/TipTapEditor';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Save, Send, X, Image as ImageIcon, Settings2, Sparkles, FileUp, Loader2, Check, Eye } from 'lucide-react';

import { API, authFetch } from '../lib/api';
import { TipTapContentRenderer } from '../components/TipTapContentRenderer';

export const WritePage = () => {
  const navigate = useNavigate();
  const { articleId } = useParams();
  const { user, loading: authLoading, login } = useAuth();
  
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [content, setContent] = useState(null);
  const [coverImage, setCoverImage] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [suggestedTags, setSuggestedTags] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  
  const [existingArticle, setExistingArticle] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [seriesList, setSeriesList] = useState([]);
  const [selectedSeriesId, setSelectedSeriesId] = useState('');
  const [newSeriesTitle, setNewSeriesTitle] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadMode, setUploadMode] = useState('replace');
  const [uploading, setUploading] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const [previewData, setPreviewData] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    if (articleId) {
      fetchArticle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleId]);

  useEffect(() => {
    if (!authLoading && !user) {
      login();
    }
  }, [authLoading, user, login]);

  const fetchArticle = useCallback(async () => {
    try {
      const response = await authFetch(`${API}/articles/${articleId}`);

      if (response.ok) {
        const data = await response.json();

        // Check if user is the author
        if (user && data.author_id !== user.user_id) {
          toast.error('You cannot edit this article');
          navigate('/');
          return;
        }

        setExistingArticle(data);
        setTitle(data.title || '');
        setSubtitle(data.subtitle || '');
        setContent(data.content || null);
        setCoverImage(data.cover_image || '');
        setTags(data.tags || []);
        setSeoTitle(data.seo_title || '');
        setSeoDescription(data.seo_description || '');
        setSelectedSeriesId(data.series_id || '');
      } else {
        toast.error('Article not found');
        navigate('/');
      }
    } catch (error) {
      console.error('Failed to fetch article:', error);
      toast.error('Failed to load article');
    }
  }, [articleId, user, navigate]);

  const handleContentChange = useCallback((newContent) => {
    setContent(newContent);
    setHasChanges(true);
  }, []);

  const handleAddTag = (e) => {
    e.preventDefault();
    const tag = tagInput.trim().toLowerCase();
    if (tag && !tags.includes(tag) && tags.length < 5) {
      setTags([...tags, tag]);
      setTagInput('');
      setHasChanges(true);
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter(t => t !== tagToRemove));
    setHasChanges(true);
  };

  const fetchSuggestedTags = async () => {
    if (!title.trim() && !content) return;
    setLoadingSuggestions(true);
    try {
      const response = await authFetch(`${API}/recommend-tags`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, content: content || {} })
      });
      if (response.ok) {
        const suggestions = await response.json();
        setSuggestedTags(suggestions.filter(t => !tags.includes(t)));
      }
    } catch (error) {
      console.error('Failed to fetch tag suggestions:', error);
    }
    setLoadingSuggestions(false);
  };

  const addSuggestedTag = (tag) => {
    if (!tags.includes(tag) && tags.length < 5) {
      setTags([...tags, tag]);
      setSuggestedTags(suggestedTags.filter(t => t !== tag));
      setHasChanges(true);
    }
  };

  const handleDocumentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();
    if (!['docx', 'pdf', 'html', 'htm'].includes(ext)) {
      toast.error('Only .docx, .pdf, and .html files are supported');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error('File too large (max 20MB)');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await authFetch(`${API}/upload-document`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setPreviewData({ ...data, fileName: file.name });
        setUploadDialogOpen(false);
        setPreviewOpen(true);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to parse document');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload document');
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleConfirmImport = () => {
    if (!previewData) return;

    if (uploadMode === 'replace') {
      setContent(previewData.content);
      if (previewData.title && !title.trim()) {
        setTitle(previewData.title);
      }
    } else {
      const existingNodes = content?.content || [];
      const newNodes = previewData.content?.content || [];
      setContent({
        type: 'doc',
        content: [...existingNodes, ...newNodes]
      });
    }

    setHasChanges(true);
    setEditorKey(prev => prev + 1);
    setPreviewOpen(false);
    setPreviewData(null);
    toast.success(`Document imported (${uploadMode === 'replace' ? 'replaced' : 'appended'})`);
  };

  const handleCancelPreview = () => {
    setPreviewOpen(false);
    setPreviewData(null);
  };

  const handleSaveDraft = async () => {
    if (!title.trim()) {
      toast.error('Please add a title');
      return;
    }

    setSaving(true);
    try {
      const articleData = {
        title: title.trim(),
        subtitle: subtitle.trim() || null,
        content: content || { type: 'doc', content: [] },
        cover_image: coverImage || null,
        tags,
        is_published: false,
        seo_title: seoTitle || null,
        seo_description: seoDescription || null,
        series_id: selectedSeriesId || null
      };

      let response;
      if (existingArticle) {
        response = await authFetch(`${API}/articles/${existingArticle.article_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(articleData)
        });
      } else {
        response = await authFetch(`${API}/articles`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(articleData)
        });
      }

      if (response.ok) {
        const savedArticle = await response.json();
        setExistingArticle(savedArticle);
        setHasChanges(false);
        toast.success('Draft saved');
        
        // Update URL if new article
        if (!articleId) {
          navigate(`/write/${savedArticle.article_id}`, { replace: true });
        }
      } else {
        let errorMsg = `Failed to save draft (${response.status})`;
        try {
          const error = await response.json();
          errorMsg = error.detail || errorMsg;
        } catch {}
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save draft: ' + (error.message || 'Network error'));
    }
    setSaving(false);
  };

  const handlePublish = async () => {
    if (!title.trim()) {
      toast.error('Please add a title');
      return;
    }

    if (!content || !content.content || content.content.length === 0) {
      toast.error('Please add some content');
      return;
    }

    setPublishing(true);
    try {
      const articleData = {
        title: title.trim(),
        subtitle: subtitle.trim() || null,
        content,
        cover_image: coverImage || null,
        tags,
        is_published: true,
        seo_title: seoTitle || title.trim(),
        seo_description: seoDescription || null,
        series_id: selectedSeriesId || null
      };

      // If creating new series, do it first
      if (newSeriesTitle.trim()) {
        try {
          const seriesRes = await authFetch(`${API}/series`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ title: newSeriesTitle.trim() })
          });
          if (seriesRes.ok) {
            const newSeries = await seriesRes.json();
            articleData.series_id = newSeries.series_id;
            setSelectedSeriesId(newSeries.series_id);
            setNewSeriesTitle('');
          }
        } catch (e) { console.error('Failed to create series:', e); }
      }

      let response;
      if (existingArticle) {
        response = await authFetch(`${API}/articles/${existingArticle.article_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(articleData)
        });
      } else {
        response = await authFetch(`${API}/articles`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(articleData)
        });
      }

      if (response.ok) {
        const publishedArticle = await response.json();
        toast.success('Article published!');
        navigate(`/article/${publishedArticle.article_id}`);
      } else {
        let errorMsg = `Failed to publish (${response.status})`;
        try {
          const error = await response.json();
          errorMsg = error.detail || errorMsg;
        } catch {}
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Failed to publish:', error);
      toast.error('Failed to publish: ' + (error.message || 'Network error'));
    }
    setPublishing(false);
    setPublishDialogOpen(false);
  };

  const handleCoverImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setCoverImage(reader.result);
      setHasChanges(true);
    };
    reader.readAsDataURL(file);
  };

  const openPublishDialog = async () => {
    setPublishDialogOpen(true);
    try {
      const response = await authFetch(`${API}/series`);
      if (response.ok) {
        setSeriesList(await response.json());
      }
    } catch (e) { /* ignore */ }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-accent border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="write-page">
      <Navbar />
      
      {/* Editor Header */}
      <div className="sticky top-16 z-40 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {existingArticle ? (
              <span>Editing draft</span>
            ) : (
              <span>New story</span>
            )}
            {hasChanges && <span className="text-accent">· Unsaved changes</span>}
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setUploadDialogOpen(true)}
              data-testid="upload-doc-btn"
              className="press-scale"
            >
              <FileUp className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Import</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSettingsOpen(true)}
              data-testid="settings-btn"
            >
              <Settings2 className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSaveDraft}
              disabled={saving}
              data-testid="save-draft-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save draft'}
            </Button>
            <Button
              size="sm"
              onClick={openPublishDialog}
              className="rounded-none"
              data-testid="publish-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Publish
            </Button>
          </div>
        </div>
      </div>

      {/* Editor */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Cover Image */}
        <div className="mb-8">
          {coverImage ? (
            <div className="relative group">
              <img
                src={coverImage}
                alt="Cover"
                className="w-full h-64 object-cover rounded-lg"
              />
              <button
                onClick={() => { setCoverImage(''); setHasChanges(true); }}
                className="absolute top-2 right-2 p-2 bg-background/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid="remove-cover-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <label className="flex items-center justify-center gap-2 h-32 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-accent transition-colors">
              <ImageIcon className="w-5 h-5 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Add cover image</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleCoverImageUpload}
                className="hidden"
                data-testid="cover-image-input"
              />
            </label>
          )}
        </div>

        {/* Title */}
        <input
          type="text"
          value={title}
          onChange={(e) => { setTitle(e.target.value); setHasChanges(true); }}
          placeholder="Title"
          className="w-full text-4xl sm:text-5xl font-bold bg-transparent border-0 outline-none mb-4 placeholder:text-muted-foreground/50"
          style={{ fontFamily: 'Fraunces, serif' }}
          data-testid="title-input"
        />

        {/* Subtitle */}
        <input
          type="text"
          value={subtitle}
          onChange={(e) => { setSubtitle(e.target.value); setHasChanges(true); }}
          placeholder="Subtitle (optional)"
          className="w-full text-xl text-muted-foreground bg-transparent border-0 outline-none mb-8 placeholder:text-muted-foreground/50"
          data-testid="subtitle-input"
        />

        {/* Tags */}
        <div className="mb-8">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {tags.map(tag => (
              <Badge 
                key={tag} 
                variant="secondary" 
                className="rounded-full gap-1"
              >
                {tag}
                <button onClick={() => handleRemoveTag(tag)} className="ml-1">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
            {tags.length < 5 && (
              <form onSubmit={handleAddTag} className="inline-flex">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Add tag..."
                  className="w-24 text-sm bg-transparent border-0 outline-none placeholder:text-muted-foreground/50"
                  data-testid="tag-input"
                />
              </form>
            )}
          </div>
          <p className="text-xs text-muted-foreground">Add up to 5 tags</p>
          {tags.length < 5 && (
            <div className="mt-2">
              <button
                type="button"
                onClick={fetchSuggestedTags}
                disabled={loadingSuggestions}
                className="text-xs text-accent hover:text-accent/80 flex items-center gap-1 transition-colors"
                data-testid="suggest-tags-btn"
              >
                <Sparkles className="w-3 h-3" />
                {loadingSuggestions ? 'Finding tags...' : 'Suggest tags'}
              </button>
              {suggestedTags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2" data-testid="suggested-tags">
                  {suggestedTags.slice(0, 5).map(tag => (
                    <button
                      key={tag}
                      onClick={() => addSuggestedTag(tag)}
                      className="text-xs px-2 py-1 rounded-full border border-accent/30 text-accent hover:bg-accent/10 transition-colors"
                      data-testid={`suggested-tag-${tag}`}
                    >
                      + {tag}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Content Editor */}
        <div className="relative">
          <TipTapEditor
            key={editorKey}
            content={content}
            onChange={handleContentChange}
            placeholder="Tell your story..."
          />

          {/* Inline import prompt when editor is empty */}
          {(!content || !content.content || content.content.length === 0 ||
            (content.content.length === 1 && content.content[0].type === 'paragraph' && !content.content[0].content)) && (
            <div className="mt-6 flex items-center justify-center" data-testid="empty-editor-import">
              <button
                onClick={() => setUploadDialogOpen(true)}
                className="flex items-center gap-2 px-4 py-2.5 text-sm text-muted-foreground border border-dashed border-border rounded-lg hover:border-accent hover:text-accent transition-all duration-200"
              >
                <FileUp className="w-4 h-4" />
                Import from .docx, .pdf, or .html
              </button>
            </div>
          )}

          {/* Floating import FAB - always visible while writing */}
          <button
            onClick={() => setUploadDialogOpen(true)}
            className="fixed bottom-6 right-6 z-50 flex items-center gap-2 pl-3.5 pr-4 py-2.5 bg-accent text-accent-foreground rounded-full shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-200 group"
            data-testid="floating-import-btn"
            title="Import document"
          >
            <FileUp className="w-4 h-4" />
            <span className="text-sm font-medium">Import</span>
          </button>
        </div>
      </main>

      {/* Publish Dialog */}
      <Dialog open={publishDialogOpen} onOpenChange={setPublishDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="publish-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fraunces, serif' }}>Ready to publish?</DialogTitle>
            <DialogDescription>
              Your article will be visible to everyone once published.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Preview</Label>
              <div className="p-4 bg-muted/30 rounded-lg">
                <h3 className="font-semibold mb-1">{title || 'Untitled'}</h3>
                {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
                {tags.length > 0 && (
                  <div className="flex gap-1 mt-2">
                    {tags.map(tag => (
                      <Badge key={tag} variant="secondary" className="text-xs rounded-full">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Series Selection */}
            <div className="space-y-2">
              <Label>Series (optional)</Label>
              <select
                value={selectedSeriesId}
                onChange={(e) => {
                  setSelectedSeriesId(e.target.value);
                  setNewSeriesTitle('');
                }}
                className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                data-testid="series-select"
              >
                <option value="">No series</option>
                <option value="__new__">+ Create new series</option>
                {seriesList.map(s => (
                  <option key={s.series_id} value={s.series_id}>{s.title}</option>
                ))}
              </select>
              {selectedSeriesId === '__new__' && (
                <Input
                  placeholder="Series title..."
                  value={newSeriesTitle}
                  onChange={(e) => setNewSeriesTitle(e.target.value)}
                  data-testid="new-series-input"
                />
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPublishDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handlePublish} 
              disabled={publishing}
              className="rounded-none"
            >
              {publishing ? 'Publishing...' : 'Publish now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="settings-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fraunces, serif' }}>Story Settings</DialogTitle>
            <DialogDescription>
              Configure SEO and other settings for your story.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="seo-title">SEO Title</Label>
              <Input
                id="seo-title"
                value={seoTitle}
                onChange={(e) => setSeoTitle(e.target.value)}
                placeholder={title || 'Enter SEO title'}
                data-testid="seo-title-input"
              />
              <p className="text-xs text-muted-foreground">{(seoTitle || title || '').length}/60 characters</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="seo-description">SEO Description</Label>
              <Textarea
                id="seo-description"
                value={seoDescription}
                onChange={(e) => setSeoDescription(e.target.value)}
                placeholder="Enter a description for search engines..."
                rows={3}
                data-testid="seo-description-input"
              />
              <p className="text-xs text-muted-foreground">{(seoDescription || '').length}/160 characters</p>
            </div>

            {/* SEO Preview */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Google Preview</Label>
              <div className="p-3 bg-white dark:bg-zinc-900 rounded-lg border border-border" data-testid="seo-google-preview">
                <p className="text-sm text-blue-600 dark:text-blue-400 font-medium truncate">
                  {seoTitle || title || 'Your Article Title'} | The Blogs Grid
                </p>
                <p className="text-xs text-green-700 dark:text-green-500 truncate mt-0.5">
                  theblogsgrid.com/article/...
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                  {seoDescription || subtitle || 'Your SEO description will appear here. Write a compelling description to improve click-through rates from search results.'}
                </p>
              </div>
            </div>

            {/* Social Card Preview */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Social Card Preview</Label>
              <div className="rounded-lg border border-border overflow-hidden" data-testid="seo-social-preview">
                {coverImage ? (
                  <img src={coverImage} alt="Cover" className="w-full h-32 object-cover" />
                ) : (
                  <div className="w-full h-32 bg-muted/50 flex items-center justify-center">
                    <span className="text-xs text-muted-foreground">No cover image</span>
                  </div>
                )}
                <div className="p-3 bg-muted/20">
                  <p className="text-xs text-muted-foreground uppercase">theblogsgrid.com</p>
                  <p className="text-sm font-medium truncate mt-0.5">{seoTitle || title || 'Your Article Title'}</p>
                  <p className="text-xs text-muted-foreground truncate mt-0.5">
                    {seoDescription || subtitle || 'Article description'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setSettingsOpen(false)} data-testid="settings-done-btn">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Document Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Import Document</DialogTitle>
            <DialogDescription>
              Upload a Word (.docx), PDF (.pdf), or HTML (.html) file. Text, headings, formatting, and interactive content will be preserved.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Import mode</Label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setUploadMode('replace')}
                  className={`flex-1 p-3 rounded-lg border text-sm text-left transition-all ${
                    uploadMode === 'replace'
                      ? 'border-accent bg-accent/10 text-accent'
                      : 'border-border hover:border-accent/40'
                  }`}
                  data-testid="upload-mode-replace"
                >
                  <p className="font-medium">Replace</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Clear editor and insert document content</p>
                </button>
                <button
                  type="button"
                  onClick={() => setUploadMode('append')}
                  className={`flex-1 p-3 rounded-lg border text-sm text-left transition-all ${
                    uploadMode === 'append'
                      ? 'border-accent bg-accent/10 text-accent'
                      : 'border-border hover:border-accent/40'
                  }`}
                  data-testid="upload-mode-append"
                >
                  <p className="font-medium">Append</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Add document content after existing text</p>
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="doc-upload">Choose file</Label>
              <div className="relative">
                <input
                  id="doc-upload"
                  type="file"
                  accept=".docx,.pdf,.html,.htm"
                  onChange={handleDocumentUpload}
                  disabled={uploading}
                  className="w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-medium file:bg-accent/10 file:text-accent hover:file:bg-accent/20 file:cursor-pointer cursor-pointer disabled:opacity-50"
                  data-testid="doc-file-input"
                />
                {uploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-background/80 rounded-md">
                    <Loader2 className="w-5 h-5 animate-spin text-accent" />
                    <span className="ml-2 text-sm text-accent">Parsing document...</span>
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">Max 20MB. Supports .docx, .pdf, and .html</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] flex flex-col" data-testid="preview-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fraunces, serif' }} className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-accent" />
              Document Preview
            </DialogTitle>
            <DialogDescription>
              {previewData?.fileName && (
                <span className="font-medium">{previewData.fileName}</span>
              )}
              {' '}&mdash; Review the parsed content below before importing.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto border border-border rounded-lg p-6 bg-paper my-2" data-testid="preview-content">
            {previewData?.content && (
              <div className="tiptap prose prose-lg max-w-none">
                <TipTapContentRenderer content={previewData.content} />
              </div>
            )}
          </div>

          {/* Stats bar */}
          {previewData?.content?.content && (
            <div className="flex flex-wrap gap-3 text-xs text-muted-foreground" data-testid="preview-stats">
              {(() => {
                const nodes = previewData.content.content;
                const tables = nodes.filter(n => n.type === 'table').length;
                const images = nodes.filter(n => n.type === 'image').length;
                const codeBlocks = nodes.filter(n => n.type === 'codeBlock').length;
                const headings = nodes.filter(n => n.type === 'heading').length;
                const paragraphs = nodes.filter(n => n.type === 'paragraph').length;
                const htmlEmbeds = nodes.filter(n => n.type === 'htmlEmbed').length;
                return (
                  <>
                    {headings > 0 && <span>{headings} heading{headings > 1 ? 's' : ''}</span>}
                    {paragraphs > 0 && <span>{paragraphs} paragraph{paragraphs > 1 ? 's' : ''}</span>}
                    {tables > 0 && <span>{tables} table{tables > 1 ? 's' : ''}</span>}
                    {images > 0 && <span>{images} image{images > 1 ? 's' : ''}</span>}
                    {codeBlocks > 0 && <span>{codeBlocks} code block{codeBlocks > 1 ? 's' : ''}</span>}
                    {htmlEmbeds > 0 && <span>{htmlEmbeds} interactive HTML embed{htmlEmbeds > 1 ? 's' : ''}</span>}
                  </>
                );
              })()}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={handleCancelPreview} data-testid="preview-cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleConfirmImport} className="gap-2" data-testid="preview-confirm-btn">
              <Check className="w-4 h-4" />
              {uploadMode === 'replace' ? 'Replace content' : 'Append content'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WritePage;
